'use client';

/**
 * GlassImageUpload Component
 *
 * A drag-and-drop image upload area with thumbnail preview, progress indicator,
 * replace/remove controls, file validation, and i18n support.
 * Follows the Glass design language with frosted borders and hover glow effects.
 */

import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React, { forwardRef, useCallback, useRef, useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { transitionPresets } from '../../theme/transitions';
import { iconSizes } from '../../theme/typography';

// ============================================================================
// Types
// ============================================================================

export interface GlassImageUploadProps {
  /** Current image URL */
  value?: string | null;
  /** Called when image changes (null = removed) */
  onChange: (url: string | null) => void;
  /** Called when user selects a file — parent handles the actual upload and returns the public URL */
  onUploadRequest: (file: File) => Promise<string>;
  /** Label above the upload area */
  label?: string;
  /** Accepted file types (default: image/jpeg,image/png,image/webp,image/svg+xml) */
  accept?: string;
  /** Maximum file size in MB (default: 5) */
  maxSizeMb?: number;
  /** Aspect ratio for preview (e.g., '16/9', '1/1') */
  aspectRatio?: string;
  /** Height of the upload area in px */
  height?: number;
  /** Disabled state */
  disabled?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_ACCEPT = 'image/jpeg,image/png,image/webp,image/svg+xml';
const DEFAULT_MAX_SIZE_MB = 5;
const DEFAULT_HEIGHT = 200;

// ============================================================================
// Component
// ============================================================================

export const GlassImageUpload = forwardRef<HTMLDivElement, GlassImageUploadProps>(
  (
    {
      value,
      onChange,
      onUploadRequest,
      label,
      accept = DEFAULT_ACCEPT,
      maxSizeMb = DEFAULT_MAX_SIZE_MB,
      aspectRatio,
      height = DEFAULT_HEIGHT,
      disabled = false,
    },
    ref,
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const { t } = useI18n();

    const [isUploading, setIsUploading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const hasImage = Boolean(value);

    // ── File Validation ──
    const validateFile = useCallback(
      (file: File): string | null => {
        const acceptedTypes = accept.split(',').map((t) => t.trim());
        if (!acceptedTypes.includes(file.type)) {
          return t('website.upload.invalidFormat');
        }
        const maxBytes = maxSizeMb * 1024 * 1024;
        if (file.size > maxBytes) {
          return t('website.upload.fileTooLarge');
        }
        return null;
      },
      [accept, maxSizeMb, t],
    );

    // ── Upload Handler ──
    const handleFile = useCallback(
      async (file: File) => {
        const validationError = validateFile(file);
        if (validationError) {
          setError(validationError);
          return;
        }

        setError(null);
        setIsUploading(true);

        try {
          const url = await onUploadRequest(file);
          onChange(url);
        } catch {
          setError(t('website.upload.uploadFailed'));
        } finally {
          setIsUploading(false);
        }
      },
      [validateFile, onUploadRequest, onChange, t],
    );

    // ── Click to Browse ──
    const handleClick = useCallback(() => {
      if (disabled || isUploading) return;
      fileInputRef.current?.click();
    }, [disabled, isUploading]);

    // ── File Input Change ──
    const handleFileChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
          handleFile(file);
        }
        // Reset input so the same file can be re-selected
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      },
      [handleFile],
    );

    // ── Drag & Drop ──
    const handleDragOver = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled && !isUploading) {
          setIsDragging(true);
        }
      },
      [disabled, isUploading],
    );

    const handleDragLeave = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    }, []);

    const handleDrop = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        if (disabled || isUploading) return;

        const file = e.dataTransfer.files?.[0];
        if (file) {
          handleFile(file);
        }
      },
      [disabled, isUploading, handleFile],
    );

    // ── Remove Image ──
    const handleRemove = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        setError(null);
        onChange(null);
      },
      [onChange],
    );

    // ── Replace Image ──
    const handleReplace = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
      fileInputRef.current?.click();
    }, []);

    // ── Border Color ──
    const borderColor = error
      ? theme.palette.error.main
      : isDragging
        ? theme.palette.primary.main
        : isDark
          ? 'rgba(255, 255, 255, 0.15)'
          : 'rgba(0, 0, 0, 0.15)';

    const hoverBorderColor = isDark ? alpha(theme.palette.primary.main, 0.5) : alpha(theme.palette.primary.main, 0.4);

    return (
      <Box ref={ref} sx={{ width: '100%' }}>
        {/* Label */}
        {label && (
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontWeight: 500,
              color: 'text.secondary',
            }}
          >
            {label}
          </Typography>
        )}

        {/* Upload Area */}
        <Box
          onClick={hasImage ? undefined : handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            position: 'relative',
            width: '100%',
            height,
            aspectRatio: aspectRatio ?? undefined,
            borderRadius: 2,
            border: `2px dashed ${borderColor}`,
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
            transition: transitionPresets.input,
            cursor: hasImage || disabled || isUploading ? 'default' : 'pointer',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: disabled ? 0.5 : 1,

            ...(!hasImage &&
              !disabled &&
              !isUploading && {
                '&:hover': {
                  borderColor: hoverBorderColor,
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                },
              }),

            ...(isDragging && {
              borderColor: theme.palette.primary.main,
              backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.08 : 0.05),
              boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
            }),
          }}
        >
          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleFileChange}
            style={{ display: 'none' }}
            disabled={disabled}
          />

          {/* Uploading State */}
          {isUploading && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <CircularProgress size={32} color="primary" />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('website.upload.uploading')}
              </Typography>
            </Box>
          )}

          {/* Empty State */}
          {!isUploading && !hasImage && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                p: 2,
              }}
            >
              <CloudUploadIcon
                sx={{
                  fontSize: iconSizes['2xl'],
                  color: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.3)',
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  textAlign: 'center',
                }}
              >
                {t('website.upload.dragDrop')}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: isDark ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.35)',
                  textAlign: 'center',
                }}
              >
                {t('website.upload.acceptedFormats')}
              </Typography>
            </Box>
          )}

          {/* Image Preview */}
          {!isUploading && hasImage && (
            <>
              <Box
                component="img"
                src={value!}
                alt={label ?? t('website.upload.title')}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />

              {/* Overlay Actions */}
              {!disabled && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    backgroundColor: 'transparent',
                    opacity: 0,
                    transition: transitionPresets.input,
                    '&:hover': {
                      opacity: 1,
                      backgroundColor: isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.4)',
                    },
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={handleReplace}
                    sx={{
                      backgroundColor: alpha(theme.palette.background.paper, 0.9),
                      color: 'text.primary',
                      '&:hover': {
                        backgroundColor: theme.palette.background.paper,
                      },
                    }}
                    title={t('website.upload.replace')}
                  >
                    <SwapHorizIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={handleRemove}
                    sx={{
                      backgroundColor: alpha(theme.palette.error.main, 0.9),
                      color: theme.palette.error.contrastText,
                      '&:hover': {
                        backgroundColor: theme.palette.error.main,
                      },
                    }}
                    title={t('website.upload.remove')}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Error Message */}
        {error && (
          <Typography
            variant="caption"
            sx={{
              mt: 0.5,
              display: 'block',
              color: 'error.main',
            }}
          >
            {error}
          </Typography>
        )}
      </Box>
    );
  },
);

GlassImageUpload.displayName = 'GlassImageUpload';

export default GlassImageUpload;
